import { z } from "zod";
import {
  actionAvailabilitySchema,
  idSchema,
  instantSchema,
  moneySchema,
  reasonSchema,
  stageSchema,
} from "@amber/contracts";
import { taskTypeSchema } from "./records.js";
import { SEVERITIES } from "./incidents.js";

export const OPERATIONS_ACTIONS = {
  pauseStage: "operations.pause_stage",
  pauseAll: "operations.pause_all",
  resume: "operations.resume",
  editRuleValue: "operations.edit_rule_value",
} as const;

export const OPERATOR_ONLY_REASON = "Only Operators and Admins can pause or resume the pipeline";
export const ADMIN_RULES_REASON = "Only Admins can change rule values";

export const STAGE_LABELS = {
  discover: "Discover",
  download: "Download",
  archive: "Archive",
  transcribe: "Transcribe",
  speakers: "Speakers",
  ready: "Ready",
} as const;

const lastChangedSchema = z.strictObject({ by: z.string(), at: instantSchema }).nullable();

export const TASK_SCOPES = ["actionable", "all"] as const;
export const TASK_VIEWS = ["open", "done"] as const;
export const TASK_AGES = ["day", "week", "older"] as const;

export const tasksQuerySchema = z.strictObject({
  scope: z.enum(TASK_SCOPES).optional(),
  view: z.enum(TASK_VIEWS).optional(),
  type: z
    .string()
    .transform((v) => v.split(",").filter((t) => t.length > 0))
    .pipe(z.array(taskTypeSchema))
    .optional(),
  person: z.string().min(1).optional(),
  platform: z.string().min(1).optional(),
  age: z.enum(TASK_AGES).optional(),
  q: z.string().trim().min(1).max(200).optional(),
});
export type TasksQuery = z.infer<typeof tasksQuerySchema>;

export const taskPersonSchema = z.strictObject({ key: z.string(), name: z.string() });
export const handledInSchema = z.strictObject({ label: z.string(), href: z.string().nullable() });

export const quickActionSchema = z.strictObject({
  key: z.string().regex(/^[a-z][a-z_]*$/),
  label: z.string(),
  kind: z.enum(["command", "link"]),
  href: z.string().nullable(),
  needs_reason: z.boolean(),
  fields: z.array(
    z.strictObject({
      name: z.string(),
      label: z.string(),
      kind: z.enum(["money", "text", "date", "choice"]),
      options: z.array(z.strictObject({ value: z.string(), label: z.string() })).optional(),
    }),
  ),
  availability: actionAvailabilitySchema,
});
export type QuickAction = z.infer<typeof quickActionSchema>;

export const priorityViewSchema = z.strictObject({
  type_weight: z.number().int(),
  type_label: z.string(),
  age: z.number().int(),
  age_days: z.number().int(),
  impact_people: z.number().int(),
  impact_items: z.number().int(),
  people: z.number().int(),
  items: z.number().int(),
  score: z.number().int(),
  level: z.enum(["high", "medium", "low"]),
  rank: z.number().int().positive(),
  of: z.number().int().positive(),
});
export type PriorityView = z.infer<typeof priorityViewSchema>;

export const taskClosedSchema = z.strictObject({
  how: z.enum(["closed_automatically", "resolved_by_person"]),
  by: z.string(),
  reason: z.string(),
  at: instantSchema,
  action: z.string().nullable(),
});

export const taskRowSchema = z.strictObject({
  id: idSchema("tsk"),
  type: taskTypeSchema,
  type_label: z.string(),
  title: z.string(),
  context: z.string(),
  people: z.array(taskPersonSchema),
  platforms: z.array(z.string()),
  affected: z.strictObject({ count: z.number().int().nonnegative(), unit: z.string(), label: z.string() }),
  opened_at: instantSchema,
  handled_in: handledInSchema.nullable(),
  state: z.enum(["open", "done"]),
  priority: priorityViewSchema.nullable(),
  closed: taskClosedSchema.nullable(),
  can_act: z.boolean(),
  lowest_role: z.enum(["editor", "operator", "admin"]),
  quick_actions: z.array(quickActionSchema),
  revision: z.number().int().positive(),
});
export type TaskRow = z.infer<typeof taskRowSchema>;

export const facetSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export const tasksResponseSchema = z.strictObject({
  rows: z.array(taskRowSchema),
  counts: z.strictObject({
    actionable: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
    high: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    done_by_person: z.number().int().nonnegative(),
    done_automatically: z.number().int().nonnegative(),
    oldest_opened_at: instantSchema.nullable(),
    matching: z.number().int().nonnegative(),
    in_view: z.number().int().nonnegative(),
  }),
  facets: z.strictObject({
    types: z.array(facetSchema),
    people: z.array(facetSchema),
    platforms: z.array(facetSchema),
    ages: z.array(facetSchema),
  }),
  scope_note: z.string(),
});
export type TasksResponse = z.infer<typeof tasksResponseSchema>;

export const taskSummarySchema = z.strictObject({
  actionable: z.number().int().nonnegative(),
  open: z.number().int().nonnegative(),
  high: z.number().int().nonnegative(),
  oldest_opened_at: instantSchema.nullable(),
});
export type TaskSummary = z.infer<typeof taskSummarySchema>;

export const taskDetailSchema = z.strictObject({
  task: taskRowSchema,
  what_happened: z.string(),
  next_step: z.string(),
  blocks: z.string(),
  affected_list: z.array(z.strictObject({ label: z.string(), detail: z.string(), href: z.string().nullable() })),
  history: z.array(
    z.strictObject({ at: instantSchema, actor: z.string(), text: z.string(), reason: z.string().nullable() }),
  ),
  severity: z
    .strictObject({
      level: z.enum(SEVERITIES),
      code: z.enum(["S1", "S2", "S3"]),
      label: z.string(),
      meaning: z.string(),
      containment: z.string(),
    })
    .nullable(),
  coverage_window: z.strictObject({ from: instantSchema, to: instantSchema.nullable() }).nullable(),
  recovery: z
    .strictObject({ evidence: z.string(), follow_up: z.string().nullable(), by: z.string(), at: instantSchema })
    .nullable(),
  record_recovery: actionAvailabilitySchema.nullable(),
});
export type TaskDetail = z.infer<typeof taskDetailSchema>;

export const recordRecoveryRequestSchema = z.strictObject({
  evidence: z.string().trim().min(1, "Describe what recovered and how you know").max(4000),
  follow_up: z.string().trim().max(1000).optional(),
});
export type RecordRecoveryRequest = z.infer<typeof recordRecoveryRequestSchema>;

export const quickActionRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
  values: z.record(z.string(), z.string()).optional(),
});
export type QuickActionRequest = z.infer<typeof quickActionRequestSchema>;

export const quickActionResponseSchema = z.object({ closed: z.boolean() });

export const pauseViewSchema = z.strictObject({
  id: idSchema("pau"),
  level: z.enum(["global", "stage"]),
  stage: stageSchema.nullable(),
  label: z.string(),
  reason: z.string(),
  started_by: z.string(),
  started_at: instantSchema,
  waiting: z.number().int().nonnegative(),
});
export type PauseView = z.infer<typeof pauseViewSchema>;

export const activePausesSchema = z.strictObject({
  pauses: z.array(pauseViewSchema),
  resume: actionAvailabilitySchema,
});
export type ActivePauses = z.infer<typeof activePausesSchema>;

export const stageRowSchema = z.strictObject({
  stage: stageSchema,
  label: z.string(),
  state: z.enum(["running", "paused", "held_by_global"]),
  pause_id: idSchema("pau").nullable(),
  waiting: z.number().int().nonnegative(),
  oldest_waiting_at: instantSchema.nullable(),
});

export const pipelineResponseSchema = z.strictObject({
  stages: z.array(stageRowSchema),
  pauses: z.array(pauseViewSchema),
  actions: z.array(actionAvailabilitySchema),
  expiring_originals: z.number().int().nonnegative(),
  last_changed: lastChangedSchema,
  audit_count: z.number().int().nonnegative(),
});
export type PipelineResponse = z.infer<typeof pipelineResponseSchema>;

export const pauseRequestSchema = z.discriminatedUnion("level", [
  z.strictObject({ level: z.literal("stage"), stage: stageSchema, reason: reasonSchema }),
  z.strictObject({
    level: z.literal("global"),
    reason: reasonSchema,
    acknowledged: z.literal(true, "Confirm that you understand what stops"),
  }),
]);
export type PauseRequest = z.infer<typeof pauseRequestSchema>;

export const pauseResponseSchema = z.object({ id: z.string() });

export const resumePreviewSchema = z.strictObject({
  pause: pauseViewSchema,
  waiting_items: z.number().int().nonnegative(),
  estimate: moneySchema,
  estimate_basis: z.string(),
  still_paused: z.array(z.string()),
});
export type ResumePreview = z.infer<typeof resumePreviewSchema>;

export const resumeRequestSchema = z.strictObject({ reason: reasonSchema });

export const operationsOkResponseSchema = z.object({ ok: z.literal(true) });

export const ruleRowSchema = z.strictObject({
  rule: z.string(),
  group: z.string(),
  label: z.string(),
  value: z.number(),
  initial: z.boolean(),
  unit: z.string(),
  used_in: z.string(),
  min: z.number(),
  max: z.number(),
  step: z.number(),
  revision: z.number().int().positive(),
  last_changed: lastChangedSchema,
});
export type RuleRow = z.infer<typeof ruleRowSchema>;

export const ruleValuesResponseSchema = z.strictObject({
  rows: z.array(ruleRowSchema),
  actions: z.array(actionAvailabilitySchema),
  last_changed: lastChangedSchema,
  audit_count: z.number().int().nonnegative(),
});
export type RuleValuesResponse = z.infer<typeof ruleValuesResponseSchema>;

export const ruleEditRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  value: z.number(),
  reason: reasonSchema,
});
export type RuleEditRequest = z.infer<typeof ruleEditRequestSchema>;

export const ruleEditResponseSchema = z.object({ revision: z.number() });

export const rulePreviewSchema = z.strictObject({ effect: z.string() });

export const JOBS_ACTIONS = { cancel: "operations.cancel_job", dismiss: "operations.dismiss_job" } as const;
export const JOB_OPERATOR_REASON = "Only Operators and Admins can retry, reprocess or cancel jobs";

export const bulkJobViewSchema = z.strictObject({
  id: idSchema("job"),
  action: z.enum(["retry", "reprocess"]),
  label: z.string(),
  total: z.number().int().positive(),
  started: z.number().int().nonnegative(),
  state: z.enum(["queued", "running", "completed", "cancelled"]),
  reason: z.string(),
  estimated_cost: moneySchema,
  created_by: z.string(),
  created_at: instantSchema,
  cancelled: z.strictObject({ by: z.string(), at: instantSchema, reason: z.string() }).nullable(),
  actions: z.strictObject({ cancel: actionAvailabilitySchema, dismiss: actionAvailabilitySchema }),
});
export type BulkJobView = z.infer<typeof bulkJobViewSchema>;

export const bulkJobsResponseSchema = z.strictObject({ jobs: z.array(bulkJobViewSchema) });
export type BulkJobsResponse = z.infer<typeof bulkJobsResponseSchema>;

export const cancelJobRequestSchema = z.strictObject({ reason: reasonSchema });

export const BUDGET_ACTIONS = { edit: "operations.edit_budgets" } as const;
export const ADMIN_BUDGETS_REASON = "Only Admins can change budgets and limits";

const budgetToneSchema = z.enum(["ok", "attention", "failed"]);

export const allowanceRowSchema = z.strictObject({
  key: z.enum(["total", "infrastructure", "ai", "other"]),
  name: z.string(),
  covers: z.string(),
  allowance: moneySchema,
  incurred: moneySchema,
  committed: moneySchema,
  forecast: moneySchema,
  percent: z.number().int().nonnegative(),
  over: z.boolean(),
  state: z.strictObject({ tone: budgetToneSchema, label: z.string() }),
  reserve: z.strictObject({ allowance: moneySchema, used: moneySchema }),
});
export type AllowanceRow = z.infer<typeof allowanceRowSchema>;

export const spendRowSchema = z.strictObject({
  label: z.string(),
  detail: z.string().nullable(),
  incurred: moneySchema,
  committed: moneySchema,
  share: z.number().nonnegative(),
});
export type SpendRow = z.infer<typeof spendRowSchema>;

export const spendBreakdownSchema = z.strictObject({ rows: z.array(spendRowSchema), unallocated: spendRowSchema });
export type SpendBreakdown = z.infer<typeof spendBreakdownSchema>;

export const budgetsResponseSchema = z.strictObject({
  period: z.strictObject({ month: z.string(), day: z.number().int().positive(), days: z.number().int().positive() }),
  revision: z.number().int().positive(),
  allowances: z.array(allowanceRowSchema),
  thresholds: z.strictObject({
    alert_percent: z.number().int(),
    suspend_percent: z.number().int(),
    stop_percent: z.number().int(),
    effects: z.strictObject({ alert: z.string(), suspend: z.string(), stop: z.string() }),
  }),
  reserve_percent: z.number().int(),
  essential_policy: z.array(z.strictObject({ work: z.string(), detail: z.string(), treatment: z.string() })),
  reserve_log: z.array(
    z.strictObject({
      at: instantSchema,
      category: z.string(),
      amount: moneySchema,
      what: z.string(),
      task_id: idSchema("tsk").nullable(),
      open: z.boolean(),
    }),
  ),
  limits: z.strictObject({
    daily: z.strictObject({ value: moneySchema, today: moneySchema, effect: z.string() }),
    per_job: z.strictObject({ value: moneySchema, largest: moneySchema, effect: z.string() }),
    backfill: z.strictObject({ percent: z.number().int(), value: moneySchema, used: moneySchema }),
  }),
  breakdowns: z.strictObject({
    provider: spendBreakdownSchema,
    stage: spendBreakdownSchema,
    person: spendBreakdownSchema,
    platform: spendBreakdownSchema,
  }),
  total_spend: z.strictObject({ incurred: moneySchema, committed: moneySchema }),
  waiting_for_budget: z.number().int().nonnegative(),
  revisions: z.array(
    z.strictObject({
      revision: z.number().int().positive(),
      at: instantSchema,
      by: z.string(),
      before: z.string(),
      after: z.string(),
      reason: z.string(),
    }),
  ),
  last_changed: lastChangedSchema,
  audit_count: z.number().int().nonnegative(),
  actions: z.strictObject({ edit: actionAvailabilitySchema }),
});
export type BudgetsResponse = z.infer<typeof budgetsResponseSchema>;

const decimalSchema = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, "Enter an amount such as 150 or 150.50");
const percentSchema = z.number().int().min(0).max(100);

export const budgetValuesSchema = z.strictObject({
  allowances: z.strictObject({ infrastructure: decimalSchema, ai: decimalSchema, other: decimalSchema }),
  alert_percent: percentSchema.min(1).max(99),
  suspend_percent: percentSchema.min(1).max(99),
  reserve_percent: percentSchema.max(50),
  daily_limit: decimalSchema,
  per_job_limit: decimalSchema,
  backfill_percent: percentSchema,
});
export type BudgetValues = z.infer<typeof budgetValuesSchema>;

export const budgetPreviewRequestSchema = z.strictObject({ values: budgetValuesSchema });
export const budgetEditRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  values: budgetValuesSchema,
  reason: reasonSchema,
});
export type BudgetEditRequest = z.infer<typeof budgetEditRequestSchema>;

export const budgetEditResponseSchema = z.object({ revision: z.number(), resumed: z.number() });

export const budgetPreviewSchema = z.strictObject({
  effects: z.array(z.strictObject({ tone: budgetToneSchema, text: z.string() })),
  changes: z.array(z.strictObject({ label: z.string(), before: z.string(), after: z.string() })),
  invalid: z.string().nullable(),
  resumes: z.number().int().nonnegative(),
});
export type BudgetPreview = z.infer<typeof budgetPreviewSchema>;

export const budgetBannerSchema = z.strictObject({
  banner: z.strictObject({ tone: budgetToneSchema, text: z.string(), detail: z.string() }).nullable(),
});
export type BudgetBanner = z.infer<typeof budgetBannerSchema>;
